import React from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useEnv, EnvDescriptor } from '../../context/EnvContext';
import { useAppEnv } from '../../api/useAppEnv';
import { useActiveTenantEditable } from '../../context/useActiveTenantEditable';
import { colors } from '../../theme/tokens';
import EnvPill from '../../components/ui/EnvPill';

interface Props {
  isCompact: boolean;
}

/**
 * WU11 — Environment selector in the TopBar.
 *
 * The current env name is ALWAYS shown (badge + name), including compact
 * mode — this is the primary "which environment am I looking at" signal and
 * the centerpiece of the promotion flow, so it must never collapse to a bare
 * dot. Compact mode only shrinks the control's min-width.
 *
 * The badge before each env name carries the env's colour token: amber for
 * `dev01`, red for `prod`, blue for everything else. Prod also shows a
 * warning icon so it's impossible to overlook when scrolling the list.
 */
const renderOption = (env: EnvDescriptor): React.ReactNode => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <EnvPill env={env.name} size="sm" />
    {env.isProd && (
      <WarningAmberIcon
        aria-label="Production environment"
        data-testid={`env-prod-warning-${env.name}`}
        sx={{ width: 14, height: 14, color: colors.envProd }}
      />
    )}
  </Box>
);

const EnvDropdown: React.FC<Props> = ({ isCompact }) => {
  const { currentEnv, setCurrentEnv, availableEnvs } = useEnv();
  // Phase 3 — only the home (editable) env + the backend's legal remote-read
  // hops are actually browsable; selecting anything else 403s HOP_NOT_LEGAL. So
  // disable non-readable options (driven by /api/app-env, not the build-time
  // list). null while app-env loads ⇒ disable nothing (avoid a load flicker).
  const appEnv = useAppEnv();
  // WAVE G/I — union the active tenant's home into the readable set so the
  // switcher never traps the user away from their tenant's home.
  //
  // WAVE I: the backend /api/app-env now returns the full HOMEABLE_PROMOTION_ENVS
  // (incl. integ) in remoteReadableEnvs WHEN THE FLAG IS ON, so this union is
  // mostly redundant in that path. It is RETAINED as a defensive belt: (a) it is
  // a strict no-op when flag-off (homeEnv collapses to currentEnv there, already
  // editable/readable), and (b) it covers the brief window where a tenant's home
  // is known to the FE but a stale app-env response hasn't yet listed it. It can
  // never over-grant — switching to a non-legal env still 403s server-side.
  const { homeEnv } = useActiveTenantEditable();
  // ladderEnv (the env this portal runs in) is always browsable — reading it is
  // LOCAL, not a remote hop. Without it, a portal whose own env is neither the
  // editable env nor in remoteReadableEnvs (which self-filters) disables its own
  // environment in the dropdown (seen on the QA portal).
  const readable = appEnv
    ? new Set<string>([
        appEnv.ladderEnv,
        appEnv.editableEnv,
        ...appEnv.remoteReadableEnvs,
        ...(homeEnv ? [homeEnv] : []),
      ])
    : null;

  const handleChange = (e: SelectChangeEvent<string>): void => {
    setCurrentEnv(e.target.value);
  };

  return (
    <FormControl size="small" sx={{ minWidth: isCompact ? 112 : 132 }}>
      <Select
        value={currentEnv}
        onChange={handleChange}
        inputProps={{ 'aria-label': 'Environment' }}
        // `renderValue` controls the closed-state appearance so the badge
        // and (optionally) the env name show even when collapsed; the menu
        // items use the full renderer below.
        renderValue={(value) => {
          const env = availableEnvs.find((e) => e.name === value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EnvPill env={String(value)} size="sm" />
              {env?.isProd && (
                <WarningAmberIcon
                  sx={{ width: 14, height: 14, color: colors.envProd }}
                />
              )}
            </Box>
          );
        }}
        sx={{
          height: 38,
          borderRadius: '8px',
          backgroundColor: colors.bgWhite,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderStrong },
        }}
      >
        {availableEnvs.map((env) => (
          <MenuItem
            key={env.name}
            value={env.name}
            disabled={readable ? !readable.has(env.name) : false}
          >
            {renderOption(env)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default EnvDropdown;
